/**
 * Settings Context Provider
 *
 * Provides profile settings and TOS acceptance data throughout the app.
 * Uses TanStack Query for automatic retry, caching, and state management.
 *
 * Following the pattern from ScheduledMeetingsContext (SM-856).
 *
 * Features:
 * - Automatic retry with exponential backoff
 * - Caching to reduce API calls
 * - Optimistic updates for better UX
 * - Compatible with existing Jitsi Meet profile format
 *
 * @example
 * ```tsx
 * // Access profile data
 * const { profile, isLoadingProfile } = useSettings();
 *
 * // Update profile
 * const { updateProfile } = useSettings();
 * await updateProfile({ displayName: 'John Doe', gravatarEmail: 'john@example.com' });
 *
 * // Access TOS data
 * const { tosAcceptance, acceptTOS } = useSettings();
 * await acceptTOS('1.0');
 * ```
 */

import type React from 'react';
import { createContext, useCallback, useEffect, useMemo, useRef } from 'react';
import { applySyncedMeetingPreferences } from '../../services/meetingPreferencesSync';
import { settingsService, type UserSettings } from '../../services/settingsService';
import { useUser } from '../UserContext';
import { useUserPermissions } from '../UserPermissionsContext';
import { DEFAULT_CONFIG } from './helpers';
import {
  useSettingsMutation,
  useSettingsQuery,
  useTOSClearMutation,
  useTOSMetadataQuery,
  useTOSMutation,
  useTOSQuery,
} from './internal_hooks';
import type { ProfileData, SettingsContextValue, SettingsProviderProps } from './types';

/**
 * Context definition
 */
export const SettingsContext = createContext<SettingsContextValue | null>(null);

/**
 * Settings Provider Component
 */
export const SettingsProvider: React.FC<SettingsProviderProps> = ({
  children,
  config: userConfig,
}) => {
  // Get user permissions state and user state
  const { isSuccess: isUserPermissionsReady } = useUserPermissions();
  const { isAuthenticated } = useUser();

  // Merge user config with defaults
  const config = useMemo(() => ({ ...DEFAULT_CONFIG, ...userConfig }), [userConfig]);

  // Profile settings query
  const {
    data: profile,
    isLoading: isLoadingProfile,
    error: profileError,
    refetch: refetchProfile,
  } = useSettingsQuery(config);

  // Hydrate meeting preferences from the backend at most once per mount: the
  // server is the source of truth on load, but a later refetch must not discard
  // edits the user has made in the meantime (SM-1168).
  const hasHydratedMeetingsRef = useRef(false);

  // Sync fetched profile (including defaultMeetingId) into settingsService so settings.profile is up to date
  useEffect(() => {
    if (!profile) {
      return;
    }
    const { meetings, ...profileFields } = profile;
    settingsService.updateSettings({
      profile: profileFields as UserSettings['profile'],
    });

    if (hasHydratedMeetingsRef.current) {
      return;
    }
    hasHydratedMeetingsRef.current = true;
    // Nothing stored yet: keep the local values and let the next save seed them.
    if (meetings) {
      void applySyncedMeetingPreferences(meetings);
    }
  }, [profile]);

  // TOS query
  const {
    data: tosAcceptance,
    isLoading: isLoadingTos,
    error: tosError,
    refetch: refetchTos,
  } = useTOSQuery();

  // TOS metadata query
  const {
    data: tosMetadata,
    isLoading: isLoadingTosMetadata,
    refetch: refetchTosMetadata,
  } = useTOSMetadataQuery();

  // Profile mutation
  const profileMutation = useSettingsMutation();

  // TOS mutations
  const tosMutation = useTOSMutation();
  const tosClearMutation = useTOSClearMutation();

  /**
   * Update profile data
   */
  const updateProfile = useCallback(
    async (newProfile: Partial<ProfileData>) => {
      await profileMutation.mutateAsync(newProfile);
    },
    [profileMutation],
  );

  /**
   * Accept TOS (compatible with current Jitsi Meet format)
   * Stores: {"accepted":"2023-02-01T17:42:37.084Z"}
   */
  const acceptTos = useCallback(async () => {
    const acceptance = {
      accepted: new Date().toISOString(),
    };
    await tosMutation.mutateAsync(acceptance);
  }, [tosMutation]);

  /**
   * Clear TOS acceptance (remove acceptance from backend)
   */
  const clearTos = useCallback(async () => {
    await tosClearMutation.mutateAsync();
  }, [tosClearMutation]);

  /**
   * Refetch profile from backend
   */
  const handleRefetchProfile = useCallback(async () => {
    await refetchProfile();
  }, [refetchProfile]);

  /**
   * Refetch TOS from backend
   */
  const handleRefetchTos = useCallback(async () => {
    await refetchTos();
    await refetchTosMetadata();
  }, [refetchTos, refetchTosMetadata]);

  /**
   * Check if backend is connected
   * Backend is connected when API Manager is initialized and user is authenticated
   * This is the correct way to check - not based on whether data has loaded yet
   */
  const isBackendConnected = useMemo(() => {
    return isAuthenticated && isUserPermissionsReady;
  }, [isAuthenticated, isUserPermissionsReady]);

  /**
   * Get last sync time from profile metadata
   */
  const lastSyncTime = useMemo(() => {
    // This could be enhanced to track actual sync time
    return null;
  }, []);

  /**
   * Context value
   */
  const contextValue: SettingsContextValue = useMemo(
    () => ({
      // Profile data
      profile: profile || null,
      isLoadingProfile,
      profileError: profileError || null,

      // TOS data
      tosAcceptance: tosAcceptance || null,
      tosMetadata: tosMetadata || null,
      isLoadingTOS: isLoadingTos || isLoadingTosMetadata,
      tosError: tosError || null,

      // Mutations
      updateProfile,
      acceptTOS: acceptTos,
      clearTOS: clearTos,

      // Utilities
      refetchProfile: handleRefetchProfile,
      refetchTOS: handleRefetchTos,
      isBackendConnected,
      lastSyncTime,
    }),
    [
      profile,
      isLoadingProfile,
      profileError,
      tosAcceptance,
      tosMetadata,
      isLoadingTos,
      isLoadingTosMetadata,
      tosError,
      updateProfile,
      acceptTos,
      clearTos,
      handleRefetchProfile,
      handleRefetchTos,
      isBackendConnected,
      lastSyncTime,
    ],
  );

  return <SettingsContext.Provider value={contextValue}>{children}</SettingsContext.Provider>;
};
